import { DateTime } from '@ksyos/date-time';
import { Alert, Box } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useContext, useState } from 'react';
import type { Gender, MaritalStatus } from '@common/enums/';
import type { ServiceForSelectedServiceType } from '@common/interfaces/processStartWizard';
import type { ValidationErrors } from '@frontend/utils/validation';
import { FoundPatientStatus } from '@common/enums/';
import { patientSearchStartBarSchema, patientSearchStartBarUpdateSchema } from '@common/validation/patient';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils/validation';
import type { ISelectedService } from '../../home/newProcess/newProcessInterfaces';
import { Loader } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { DuplicatePatientIdAlert } from '../DuplicatePatientIdAlert';
import { NoPatientResultBar, PatientSearchField } from './NoPatientResultBar';
import { PatientResultBar } from './PatientResultBar';
import { PatientSearchBar } from './PatientSearchBar';
import { PatientSearchContext, SearchType } from './patientSearchContext';

export interface IPatientSearchResult {
    birthDate: string | null;
    careProviderOrganizationId: string | null;
    careProviderPractitionerId: string | null;
    city: string | null;
    country: string | null;
    deceasedDate: string | null;
    emailAddress: string | null;
    gender: Gender | null;
    homePhoneNumber: string | null;
    houseLetter: string | null;
    houseNumber: string | null;
    houseNumberSuffix: string | null;
    id?: string;
    identifierInsurance: string | null;
    identifierSocialSecurity: string | null;
    isActive: boolean;
    isDeceased: boolean;
    maritalStatus: MaritalStatus | null;
    mobileNumber: string | null;
    nameFamily: string | null;
    nameFamily2: string | null;
    nameGiven: string | null;
    nameInfix: string | null;
    nameInfix2: string | null;
    nameUsual: string | null;
    processNumber: string | null;
    profession: string | null;
    state: string | null;
    street: string | null;
    zip: string | null;
}

interface IPatientSearchBarProps {
    canCreateAndSeeAllPatients: boolean;
    header?: boolean;
    isPatientsList?: boolean;
    onNoBsnUrl?: string;
    onStartUrl: (patientId: string) => string;
    selectedService?: ISelectedService | ServiceForSelectedServiceType;
    text: string;
}

type UnavailableServiceWithAlertAndTooltip = Record<string, Record<string, { alertAndTooltip: string }>>;

export function PatientSearchStartBar(props: IPatientSearchBarProps) {
    const translate = getSelectedLanguage();
    const navigate = useNavigate();
    const {
        patient,
        newPatient,
        foundPatientStatus,
        duplicatePatientId,
        isSearching,
        patients,
        unavailableServices,
        setPatient,
        setNewPatient,
        setFoundPatientStatus,
        setDuplicatePatientId,
        search,
    } = useContext(PatientSearchContext);

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const closedForAllReason = findClosedForAllReason();
    const selectedServiceCode = getServiceCodeForSelectedService();

    return (
        <FormContextProvider
            value={{
                errors: errors,
                item: patient,
                onChange: handleChange,
                onDoValidate: handleValidation,
                placeholderGroupName: 'patient',
            }}
        >
            <PatientSearchBar
                canCreateAndSeeAllPatients={props.canCreateAndSeeAllPatients}
                header={props.header}
                onSearch={handleSearch}
                text={props.text}
            />
            {isSearching && !props.isPatientsList && <Loader horizontalCentered={true} />}

            {closedForAllReason && selectedServiceCode && (
                <Box
                    backgroundColor="white"
                    paddingBottom={5}
                    paddingX={5}
                >
                    <Alert
                        appearance="warning"
                        noMargin={true}
                    >
                        {
                            (translate.unavailableServices as UnavailableServiceWithAlertAndTooltip)[
                                selectedServiceCode.toLowerCase()
                            ][closedForAllReason].alertAndTooltip
                        }
                    </Alert>
                </Box>
            )}

            {!props.isPatientsList &&
                foundPatientStatus === FoundPatientStatus.foundPatientOutsideOfOrganization &&
                newPatient?.patient && (
                    <>
                        <PatientResultBar
                            closedForAllReason={findClosedForAllReason()}
                            disabled={Boolean(duplicatePatientId)}
                            icon="profile"
                            onStart={() => handleStart(newPatient.patient.id)}
                            patient={newPatient.patient}
                            unavailableService={findUnavailableServiceForPatient(newPatient.patient.id)}
                        />
                        {duplicatePatientId && <DuplicatePatientIdAlert id={duplicatePatientId} />}
                    </>
                )}
            {!props.isPatientsList &&
                foundPatientStatus === FoundPatientStatus.foundPatientInOrganization &&
                patients.map((onePatient, index) => (
                    <PatientResultBar
                        closedForAllReason={findClosedForAllReason()}
                        disabled={Boolean(duplicatePatientId)}
                        icon="profile"
                        key={onePatient.id ?? `patient-${index}`}
                        onStart={() => handleStart(onePatient.id)}
                        patient={onePatient as IPatientSearchResult}
                        unavailableService={findUnavailableServiceForPatient(onePatient.id)}
                    />
                ))}
            {foundPatientStatus === FoundPatientStatus.noPatientFound && (
                <NoPatientResultBar
                    searchField={props.canCreateAndSeeAllPatients ? PatientSearchField.birthDateAndBsn : undefined}
                />
            )}
        </FormContextProvider>
    );

    function findUnavailableServiceForPatient(patientId?: string) {
        return patientId
            ? unavailableServices?.[patientId]?.find((item) =>
                  props.selectedService && 'serviceId' in props.selectedService
                      ? item.serviceId === props.selectedService?.serviceId
                      : item.serviceId === props.selectedService?.id
              )
            : undefined;
    }

    function findClosedForAllReason() {
        const closedForAllReasons = ['closedOutsideOfBusinessHours'];
        let firstClosedForAllReason: string | undefined;

        Object.values(unavailableServices ?? {}).find(function (unavailableServiceForPatient) {
            const foundReason = unavailableServiceForPatient.find(
                (entry) =>
                    closedForAllReasons.includes(entry.reason) &&
                    (props.selectedService && 'serviceId' in props.selectedService
                        ? entry.serviceId === props.selectedService?.serviceId
                        : entry.serviceId === props.selectedService?.id)
            );

            if (foundReason) {
                firstClosedForAllReason = foundReason.reason;
            }

            return foundReason;
        });

        return firstClosedForAllReason;
    }

    function getServiceCodeForSelectedService() {
        if (!props.selectedService) {
            return;
        }

        if ('code' in props.selectedService) {
            return props.selectedService.code;
        }

        if ('serviceCode' in props.selectedService) {
            return props.selectedService.serviceCode;
        }
    }

    function handleChange<K extends keyof IPatientSearchResult>(name: K, value: IPatientSearchResult[K]) {
        const cleanedValue =
            name === 'birthDate' && typeof value === 'string' ? DateTime.from(value).format('YYYY-MM-DD') : value;
        if (patient[name] !== value) {
            setNewPatient(null);
            setDuplicatePatientId(undefined);
            setPatient({ ...patient, [name]: cleanedValue });
            setFoundPatientStatus(FoundPatientStatus.noSearch);
        }
    }

    function handleValidation(key: keyof IPatientSearchResult) {
        if (key === 'birthDate') {
            const dateToValidate = patient.birthDate ? DateTime.from(patient.birthDate).format('YYYY-MM-DD') : null;
            setErrors(
                updateValidationErrorsForOne(
                    patientSearchStartBarSchema,
                    { ...patient, birthDate: dateToValidate },
                    errors,
                    'birthDate'
                )
            );

            if (dateToValidate && DateTime.from(dateToValidate).isAfter(DateTime.now(), 'day')) {
                setErrors(errors.set('birthDate', 'date_future'));
            }
        }
        if (key !== 'birthDate') {
            validateOne(key);
        }
    }

    function validateOne(name: keyof IPatientSearchResult) {
        setErrors(updateValidationErrorsForOne(patientSearchStartBarSchema, patient, errors, name));
    }

    function handleStart(patientId?: string) {
        if (patientId) {
            const startUrl = props.onStartUrl(patientId);

            return navigate(startUrl);
        }

        handleCreatePatient();
    }

    async function handleCreatePatient() {
        const { item, hasErrors } = validate(patientSearchStartBarUpdateSchema, newPatient?.patient);
        if (!hasErrors) {
            const result = await fetchJson('/api/patients', 'POST', {
                patient: { ...item },
            });
            const startUrl = props.onStartUrl(result.json.id);
            navigate(startUrl);
        }
    }

    function handleSearch() {
        const patientToValidate =
            patient.birthDate !== null
                ? { ...patient, birthDate: DateTime.from(patient.birthDate).format('YYYY-MM-DD') }
                : patient;
        const validationResult = validate(patientSearchStartBarSchema, patientToValidate);
        const validationErrors = validateIfDateInTheFuture(patientToValidate.birthDate, validationResult.errors);
        setErrors(validationErrors);

        if (validationErrors.size === 0) {
            search(
                props.canCreateAndSeeAllPatients ? SearchType.startBar : SearchType.startBarOnlyInOrganization,
                patientToValidate
            );
        }
    }

    function validateIfDateInTheFuture(date: string | null, currentErrors: ValidationErrors): ValidationErrors {
        const dateInTheFutureErrorKey = 'date_future';

        if (date && DateTime.from(date).isAfter(DateTime.now(), 'day')) {
            return currentErrors.set('birthDate', dateInTheFutureErrorKey);
        } else if (currentErrors.get('birthDate') === dateInTheFutureErrorKey) {
            return currentErrors.remove('birthDate');
        }

        return currentErrors;
    }
}
